/**
 * One active browser *tab* at a time, without rotating the JWT / Redis jti.
 *
 * Duplicate-tab cannot mint a new jti (no silent re-login). Instead the new tab
 * "claims" ownership via localStorage + BroadcastChannel; the previous tab
 * logs out locally and must NOT POST /auth/logout (that would revoke Redis and
 * kill the new tab's still-valid JWT).
 *
 * Tab id is in-memory only. Chrome/Edge copy sessionStorage on duplicate, so a
 * persisted tab id would be shared by both tabs.
 */

export const SESSION_OWNER_TAB_ID_KEY = "@WalletBC:sessionOwnerTabId";
export const SESSION_TAB_CHANNEL = "walletbc-session-tab";

const createTabId = (): string => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `tab-${Date.now().toString(16)}-${Math.random().toString(16).slice(2)}`;
};

/** Unique per JS context — never reuse a value copied from another tab. */
export const sessionTabId = createTabId();

export type SessionTabClaimMessage = {
  type: "claim";
  tabId: string;
  at: number;
};

export const parseSessionOwnerTabId = (raw: string | null | undefined): string | null => {
  if (!raw || typeof raw !== "string") return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;

  if (trimmed.startsWith("{")) {
    try {
      const parsed = JSON.parse(trimmed) as { tabId?: unknown };
      return typeof parsed?.tabId === "string" && parsed.tabId.trim()
        ? parsed.tabId.trim()
        : null;
    } catch {
      return null;
    }
  }

  return trimmed;
};

export const readSessionOwnerTabId = (): string | null => {
  try {
    return parseSessionOwnerTabId(localStorage.getItem(SESSION_OWNER_TAB_ID_KEY));
  } catch {
    return null;
  }
};

export const isForeignSessionOwner = (ownerTabId: string | null | undefined): boolean =>
  Boolean(ownerTabId && ownerTabId !== sessionTabId);

const postClaim = () => {
  if (typeof BroadcastChannel === "undefined") return;
  try {
    const channel = new BroadcastChannel(SESSION_TAB_CHANNEL);
    const message: SessionTabClaimMessage = {
      type: "claim",
      tabId: sessionTabId,
      at: Date.now(),
    };
    channel.postMessage(message);
    channel.close();
  } catch {
    // BroadcastChannel can fail in some private / opaque origins.
  }
};

/** Mark this tab as the sole UI owner of the current browser JWT. */
export const claimSessionTab = (): void => {
  const payload = JSON.stringify({ tabId: sessionTabId, at: Date.now() });
  try {
    localStorage.setItem(SESSION_OWNER_TAB_ID_KEY, payload);
  } catch {
    // Quota / disabled storage — BroadcastChannel may still notify peers.
  }
  postClaim();
};

/** Drop ownership if this tab is the recorded owner (full logout). */
export const releaseSessionTabClaim = (): void => {
  try {
    if (readSessionOwnerTabId() === sessionTabId) {
      localStorage.removeItem(SESSION_OWNER_TAB_ID_KEY);
    }
  } catch {
    // ignore
  }
};

type SubscribeOptions = {
  onForeignClaim: () => void;
};

/**
 * Listen for another tab claiming the session.
 * `storage` covers cross-tab localStorage; BroadcastChannel covers same-origin
 * tabs that may miss storage events (and notifies faster).
 */
export const subscribeSessionTabClaims = ({
  onForeignClaim,
}: SubscribeOptions): (() => void) => {
  const handleOwner = (ownerTabId: string | null) => {
    if (isForeignSessionOwner(ownerTabId)) {
      onForeignClaim();
    }
  };

  const onStorage = (event: StorageEvent) => {
    if (event.key !== SESSION_OWNER_TAB_ID_KEY) return;
    handleOwner(parseSessionOwnerTabId(event.newValue));
  };

  window.addEventListener("storage", onStorage);

  let channel: BroadcastChannel | null = null;
  if (typeof BroadcastChannel !== "undefined") {
    try {
      channel = new BroadcastChannel(SESSION_TAB_CHANNEL);
      channel.onmessage = (event: MessageEvent<SessionTabClaimMessage>) => {
        const tabId = event?.data?.tabId;
        if (typeof tabId === "string") {
          handleOwner(tabId);
        }
      };
    } catch {
      channel = null;
    }
  }

  return () => {
    window.removeEventListener("storage", onStorage);
    try {
      channel?.close();
    } catch {
      // ignore
    }
  };
};
